import { Fragment, useMemo, useState } from 'react';
import { Stat } from '../components/data';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface SalesOrder {
  id: number;
  title: string | null;
  date: string | null;
  status: string | null;
}
interface Opportunity {
  id: number;
  title: string | null;
  date: string | null;
}
/** How a firewall was tied to its opportunity, best evidence first. */
type MatchKind = 'line' | 'serial' | 'product' | 'model';
interface Sale {
  source: MatchKind | null;
  price: number | null;
  cost: number | null;
  profit: number | null;
  margin: number | null;
  sharedBy: number;
  opportunity: Opportunity | null;
  salesOrder: SalesOrder | null;
  lineName: string | null;
  quoteNumber: string | null;
}
interface Renewal {
  date: string | null;
  state: 'expired' | 'soon' | 'ok' | 'unknown';
  days: number | null;
  /** "opportunity" = its own renewal opportunity; "contract" = the contract service it sits on. */
  source: 'opportunity' | 'contract' | null;
  opportunity: Opportunity | null;
  salesOrder: SalesOrder | null;
  sharedBy: number;
  service: string | null;
  period: string | null;
  annualPrice: number | null;
  annualCost: number | null;
  annualProfit: number | null;
  margin: number | null;
}
interface Firewall {
  id: number;
  clientId: number;
  client: string;
  name: string;
  type: string | null;
  category: string | null;
  product: string | null;
  productId: number | null;
  serial: string | null;
  reference: string | null;
  location: string | null;
  installed: string | null;
  sale: Sale;
  renewal: Renewal;
}
interface Payload {
  fetchedUtc: string;
  activeCompanies: number;
  configurationItems: number;
  soonDays: number;
  /** False when Autotask refused cost to this API user — profit stands down. */
  costVisible: boolean;
  rows: Firewall[];
  totals: {
    firewalls: number;
    clients: number;
    price: number;
    cost: number;
    profit: number;
    annualRenewalPrice: number;
    annualRenewalCost: number;
    annualRenewalProfit: number;
    fromLine: number;
    fromSerial: number;
    fromProduct: number;
    fromModel: number;
    noSale: number;
    shared: number;
    withOrder: number;
    renewalFromOpportunity: number;
    renewalFromContract: number;
    expired: number;
    soon: number;
    noRenewalDate: number;
    noRenewalMoney: number;
    noProduct: number;
    wonOpportunities: number;
  };
  notes: string[];
  unavailable: { entity: string; reason: string }[];
  source: { entities: string; filter: string; fields: string; formula: string };
}

const money = (v: number | null) => (v === null ? '—' : formatNumber(v, 'currency'));
const pct = (v: number | null) => (v === null ? '—' : `${formatNumber(v, 'number')}%`);
const day = (iso: string | null) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

/* What tied this firewall to its money, said plainly. The order matters to the
   reader: a quote line is that box's own, a model name is a good guess. */
const MATCH_LABEL: Record<MatchKind, string> = {
  line: 'its own quote line',
  serial: 'an opportunity naming its serial',
  product: 'an opportunity for its product',
  model: 'an opportunity naming its model',
};
const MATCH_SHORT: Record<MatchKind, string> = {
  line: 'own quote line',
  serial: 'by serial',
  product: 'by product',
  model: 'by model',
};

/** The renewal cell — expired is the one that needs someone today. */
function RenewalCell({ r, soonDays }: { r: Renewal; soonDays: number }) {
  if (r.date === null) return <span className="muted">no date</span>;
  if (r.state === 'expired') {
    return (
      <>
        <span className="badge badge-critical">{day(r.date)}</span>
        <div className="muted xs">expired {Math.abs(r.days ?? 0)} days ago</div>
      </>
    );
  }
  if (r.state === 'soon') {
    return (
      <>
        <span className="badge badge-warning">{day(r.date)}</span>
        <div className="muted xs">in {r.days} days</div>
      </>
    );
  }
  return (
    <>
      {day(r.date)}
      <div className="muted xs">more than {soonDays} days</div>
    </>
  );
}

const STATES: { k: '' | 'expired' | 'soon' | 'ok' | 'unknown'; label: string }[] = [
  { k: '', label: 'Any renewal date' },
  { k: 'expired', label: 'Expired' },
  { k: 'soon', label: 'Due soon' },
  { k: 'ok', label: 'Current' },
  { k: 'unknown', label: 'No renewal date' },
];
const SOURCES: { k: '' | MatchKind | 'none'; label: string }[] = [
  { k: '', label: 'Any sale' },
  { k: 'line', label: 'Its own quote line' },
  { k: 'serial', label: 'Matched on serial' },
  { k: 'product', label: 'Matched on product' },
  { k: 'model', label: 'Matched on model' },
  { k: 'none', label: 'No sale found' },
];

/**
 * Reports › Firewall Assessment — every ACTIVE firewall in the estate
 * (routes/reportsFirewalls.ts): the opportunity and sales order it was sold on
 * with the price, cost and profit of that sale, then when it comes up for
 * renewal and the annual profit on the renewal.
 *
 * The money is on the OPPORTUNITY, for both the sale and the renewal — a
 * renewal is its own opportunity sitting beside the sale on the same client,
 * and the sales order hangs off the opportunity rather than off the device.
 * Because most devices carry no link back to a quote, a row always says how it
 * was tied to its money, and the weaker ties are worth reading as such.
 */
export function FirewallAssessmentPage() {
  const [client, setClient] = useState('');
  const [search, setSearch] = useState('');
  const [state, setState] = useState<'' | 'expired' | 'soon' | 'ok' | 'unknown'>('');
  const [saleSource, setSaleSource] = useState<'' | MatchKind | 'none'>('');
  /* Its own sort: the app-wide heading sort would split a firewall from its detail row. */
  const [sortBy, setSortBy] = useState<'client' | 'renewal' | 'profit' | 'price' | 'renewalProfit'>('client');
  const [open, setOpen] = useState<Set<number>>(new Set());
  const data = useHeldResource<Payload>('/reports/firewalls');
  const d = data.data;
  const costVisible = d?.costVisible ?? true;

  const clients = useMemo(() => {
    const seen = new Map<number, string>();
    for (const r of d?.rows ?? []) seen.set(r.clientId, r.client);
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [d]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.rows ?? [])
      .filter(
        (r) =>
          (!client || String(r.clientId) === client) &&
          (!state || r.renewal.state === state) &&
          (!saleSource || (saleSource === 'none' ? r.sale.source === null : r.sale.source === saleSource)) &&
          (!q ||
            r.name.toLowerCase().includes(q) ||
            r.client.toLowerCase().includes(q) ||
            (r.product ?? '').toLowerCase().includes(q) ||
            (r.serial ?? '').toLowerCase().includes(q) ||
            (r.sale.opportunity?.title ?? '').toLowerCase().includes(q) ||
            (r.renewal.opportunity?.title ?? '').toLowerCase().includes(q) ||
            (r.sale.quoteNumber ?? '').toLowerCase().includes(q) ||
            (r.sale.salesOrder?.title ?? '').toLowerCase().includes(q)),
      )
      .sort((a, b) => {
        const n = (v: number | null) => v ?? -Infinity;
        switch (sortBy) {
          case 'renewal':
            return (a.renewal.date ?? '9999').localeCompare(b.renewal.date ?? '9999') || a.client.localeCompare(b.client);
          case 'profit':
            return n(b.sale.profit) - n(a.sale.profit) || a.client.localeCompare(b.client);
          case 'price':
            return n(b.sale.price) - n(a.sale.price) || a.client.localeCompare(b.client);
          case 'renewalProfit':
            return n(b.renewal.annualProfit) - n(a.renewal.annualProfit) || a.client.localeCompare(b.client);
          default:
            return a.client.localeCompare(b.client) || a.name.localeCompare(b.name);
        }
      });
  }, [d, client, search, state, saleSource, sortBy]);

  const sum = (pick: (r: Firewall) => number | null) => shown.reduce((t, r) => t + (pick(r) ?? 0), 0);
  const price = sum((r) => r.sale.price);
  const profit = sum((r) => r.sale.profit);
  const renewalPrice = sum((r) => r.renewal.annualPrice);
  const renewalProfit = sum((r) => r.renewal.annualProfit);
  const marginOf = (p: number, base: number) => (base > 0 ? Math.round((p / base) * 100) : null);
  const cols = costVisible ? 10 : 8;

  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const exportCsv = () =>
    downloadCsv(`firewall_assessment_${fileStamp()}.csv`, [
      [
        'Client',
        'Firewall',
        'Product',
        'Serial',
        'Installed',
        'Tied to its sale by',
        'Sale opportunity',
        'Sale opportunity date',
        'Sales order',
        'Order date',
        'Order status',
        'Quote',
        'Quote line',
        'Price',
        'Cost',
        'Profit',
        'Margin %',
        'Shared by',
        'Renews',
        'Renewal state',
        'Renewal from',
        'Renewal opportunity',
        'Contract service',
        'Annual renewal',
        'Annual cost',
        'Annual profit',
        'Annual margin %',
      ],
      ...shown.map((r) => [
        r.client,
        r.name,
        r.product ?? '',
        r.serial ?? '',
        r.installed ?? '',
        r.sale.source ?? 'none',
        r.sale.opportunity?.title ?? '',
        r.sale.opportunity?.date ?? '',
        r.sale.salesOrder?.title ?? '',
        r.sale.salesOrder?.date ?? '',
        r.sale.salesOrder?.status ?? '',
        r.sale.quoteNumber ?? '',
        r.sale.lineName ?? '',
        r.sale.price ?? '',
        r.sale.cost ?? '',
        r.sale.profit ?? '',
        r.sale.margin ?? '',
        r.sale.sharedBy,
        r.renewal.date ?? '',
        r.renewal.state,
        r.renewal.source ?? 'none',
        r.renewal.opportunity?.title ?? '',
        r.renewal.service ?? '',
        r.renewal.annualPrice ?? '',
        r.renewal.annualCost ?? '',
        r.renewal.annualProfit ?? '',
        r.renewal.margin ?? '',
      ]),
    ]);

  return (
    <>
      <PageHead
        title="Firewall Assessment"
        lede="Every active firewall in the estate, read live from Autotask: the opportunity and sales order it was sold on with the price, cost and profit of that sale, then when it comes up for renewal and the annual profit on the renewal."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Client">
              <select className="select" value={client} onChange={(e) => setClient(e.target.value)} style={{ minWidth: 260 }}>
                <option value="">All clients with a firewall</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sale">
              <select className="select" value={saleSource} onChange={(e) => setSaleSource(e.target.value as typeof saleSource)}>
                {SOURCES.map((s) => (
                  <option key={s.k} value={s.k}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Renewal">
              <select className="select" value={state} onChange={(e) => setState(e.target.value as typeof state)}>
                {STATES.map((s) => (
                  <option key={s.k} value={s.k}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Search">
              <input
                className="input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Firewall, client, product, serial, opportunity or order"
              />
            </Field>
            <Field label="Sort by">
              <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                <option value="client">Client, A–Z</option>
                <option value="renewal">Renewal, soonest first</option>
                {costVisible && <option value="profit">Profit, highest first</option>}
                <option value="price">Price, highest first</option>
                {costVisible && <option value="renewalProfit">Renewal profit, highest first</option>}
              </select>
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!shown.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>

          <p className="xs muted mt-2">
            Active configuration items that are firewalls, on active clients — the same ones the client page&rsquo;s &ldquo;Firewall renewals&rdquo; dial
            counts. An inactive item is a box that has been retired or replaced, and is left out.
            {d && ` ${num(d.configurationItems)} active configuration items across ${num(d.activeCompanies)} active clients were read.`}
          </p>
          <p className="xs muted mt-1">
            The money is on the <strong>opportunity</strong> the firewall was sold on, and the sales order hangs off that opportunity — Autotask&rsquo;s sales
            order carries dates, a status and addresses but no price or cost of its own, and a configuration item has no sales order to follow. Opportunities
            that were lost are left out.
            {d && ` ${num(d.totals.wonOpportunities)} won opportunities at those clients were read.`}
          </p>
          {d && (
            <p className="xs muted mt-1">
              How each firewall was tied to its money: {num(d.totals.fromLine)} by {d.totals.fromLine === 1 ? 'its' : 'their'} own quote line (exact),{' '}
              {num(d.totals.fromSerial)} by serial number, {num(d.totals.fromProduct)} by product, {num(d.totals.fromModel)} by model name, and{' '}
              {num(d.totals.noSale)} not at all. Only the first is a link Autotask records itself; the rest read a won opportunity at that client whose words
              name the box, which is how a person finds it.
            </p>
          )}
          {d && d.totals.shared > 0 && (
            <p className="xs muted mt-1">
              {num(d.totals.shared)} {d.totals.shared === 1 ? 'firewall shares an opportunity' : 'firewalls share an opportunity'} with another of the same
              model at the same client. Those are split evenly and the row says so, which keeps the totals right — a shared row is an average rather than a
              reading of that one box. A firewall on its own quote line is never split.
            </p>
          )}
          {d && !d.costVisible && (
            <p className="xs muted mt-1">
              <strong>Cost is hidden.</strong> Autotask returned money but no cost on every opportunity, which is what it does when the API user cannot view
              cost data in the UI. Profit and margin are left out rather than reported as 100% — an Autotask administrator can grant that permission.
            </p>
          )}
          <p className="xs muted mt-1">
            A renewal is the firewall&rsquo;s warranty expiry in Autotask. Its money is the <strong>renewal opportunity</strong> — a separate opportunity
            beside the sale, told apart by &ldquo;renewal&rdquo; in its words, by billing on a recurring period, or by service wording — taken out to a year.
            Where there is none, the contract service the firewall sits on is used instead.
            {d &&
              ` ${num(d.totals.renewalFromOpportunity)} come from a renewal opportunity and ${num(d.totals.renewalFromContract)} from a contract service.`}
            {d && d.totals.noRenewalMoney > 0 &&
              ` ${num(d.totals.noRenewalMoney)} firewall${d.totals.noRenewalMoney === 1 ? ' has' : 's have'} neither, so ${d.totals.noRenewalMoney === 1 ? 'its' : 'their'} renewal money is blank rather than guessed.`}
          </p>
          {d?.notes.map((n) => (
            <p key={n} className="xs muted mt-1">
              {n}
            </p>
          ))}
          {d?.unavailable.map((u) => (
            <p key={u.entity} className="xs muted mt-1">
              {u.entity} could not be read from Autotask — {u.reason}. Figures that come from it are blank.
            </p>
          ))}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading firewalls from Autotask" />}

      {d && (
        <>
          <div className="grid grid-4 mb-4">
            <Stat label="Firewalls" value={num(shown.length)} foot={`${num(new Set(shown.map((r) => r.clientId)).size)} clients`} />
            <Stat
              label="Sold for"
              value={money(price)}
              foot={costVisible ? `${money(sum((r) => r.sale.cost))} cost` : 'cost hidden by Autotask'}
            />
            <Stat
              label="Profit"
              value={costVisible ? money(profit) : '—'}
              foot={!costVisible ? 'the API user cannot view cost' : marginOf(profit, price) === null ? 'no sale found' : `${marginOf(profit, price)}% margin`}
            />
            <Stat
              label="Renewals to act on"
              value={num(shown.filter((r) => r.renewal.state === 'expired' || r.renewal.state === 'soon').length)}
              flagged={shown.some((r) => r.renewal.state === 'expired')}
              foot={`${num(shown.filter((r) => r.renewal.state === 'expired').length)} expired · ${num(
                shown.filter((r) => r.renewal.state === 'soon').length,
              )} within ${d.soonDays} days`}
            />
          </div>

          <div className="grid grid-3 mb-4">
            <Stat
              label="Renewals, a year"
              value={money(renewalPrice)}
              foot={costVisible ? `${money(sum((r) => r.renewal.annualCost))} cost` : 'cost hidden by Autotask'}
            />
            <Stat
              label="Renewal profit, a year"
              value={costVisible ? money(renewalProfit) : '—'}
              foot={
                !costVisible
                  ? 'the API user cannot view cost'
                  : marginOf(renewalProfit, renewalPrice) === null
                    ? 'no renewal money in Autotask'
                    : `${marginOf(renewalProfit, renewalPrice)}% margin`
              }
            />
            <Stat
              label="No sale found"
              value={num(shown.filter((r) => r.sale.source === null).length)}
              foot={`of ${num(shown.length)} firewalls · ${num(shown.filter((r) => r.sale.salesOrder !== null).length)} name a sales order`}
            />
          </div>

          <section className="card">
            <div className="table-wrap">
              <table className="table inv-table fw-table" data-nosort>
                <thead>
                  <tr>
                    {/* The three text columns carry two or three lines each, so they are
                        given room: left to itself the table squeezes "Sold on" down to one
                        word per line. The table scrolls sideways rather than wrapping. */}
                    <th style={{ width: 190 }}>Firewall</th>
                    <th style={{ width: 150 }}>Client</th>
                    <th style={{ width: 210 }}>Sold on</th>
                    <th className="num">Price</th>
                    {costVisible && <th className="num">Cost</th>}
                    {costVisible && <th className="num">Profit</th>}
                    <th className="num">Margin</th>
                    <th style={{ width: 130 }}>Renews</th>
                    <th className="num">Renewal, a year</th>
                    <th className="num">Renewal profit</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={cols} className="muted small">
                        No firewalls match these filters.
                      </td>
                    </tr>
                  )}
                  {shown.map((r) => {
                    const isOpen = open.has(r.id);
                    const detail = [r.product, r.serial && `serial ${r.serial}`, r.location].filter(Boolean).join(' · ');
                    const order = r.sale.salesOrder;
                    const opp = r.sale.opportunity;
                    return (
                      <Fragment key={r.id}>
                        <tr className={isOpen ? 'is-open' : undefined}>
                          <td>
                            {r.sale.source || r.renewal.source ? (
                              <button type="button" className="link-button" onClick={() => toggle(r.id)} aria-expanded={isOpen}>
                                {isOpen ? '▾' : '▸'} {r.name}
                              </button>
                            ) : (
                              <span className="strong">{r.name}</span>
                            )}
                            {detail && <div className="xs muted">{detail}</div>}
                          </td>
                          <td className="xs">
                            {r.client}
                            {r.installed && <div className="xs muted">installed {day(r.installed)}</div>}
                          </td>
                          <td className="xs">
                            {opp ? (
                              <>
                                {opp.title ?? `Opportunity ${opp.id}`}
                                <div className="xs muted">
                                  {order ? `${order.title ?? `Order ${order.id}`}${order.date ? ` · ${day(order.date)}` : ''}` : 'no sales order'}
                                </div>
                                {r.sale.source && (
                                  <div className="xs muted">
                                    {MATCH_SHORT[r.sale.source]}
                                    {r.sale.sharedBy > 1 ? ` · 1 of ${r.sale.sharedBy} sharing` : ''}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span className="muted">no sale found</span>
                            )}
                          </td>
                          <td className="num">{money(r.sale.price)}</td>
                          {costVisible && <td className="num">{money(r.sale.cost)}</td>}
                          {costVisible && <td className="num strong">{money(r.sale.profit)}</td>}
                          <td className="num xs">{pct(r.sale.margin)}</td>
                          <td className="xs">
                            <RenewalCell r={r.renewal} soonDays={d.soonDays} />
                          </td>
                          <td className="num">
                            {money(r.renewal.annualPrice)}
                            <div className="xs muted">
                              {r.renewal.source === 'opportunity'
                                ? (r.renewal.opportunity?.title ?? 'renewal opportunity')
                                : r.renewal.source === 'contract'
                                  ? (r.renewal.service ?? 'contract service')
                                  : 'not in Autotask'}
                            </div>
                          </td>
                          <td className="num strong">
                            {money(r.renewal.annualProfit)}
                            {r.renewal.margin !== null && <div className="xs muted">{pct(r.renewal.margin)}</div>}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="inv-line">
                            {/* One cell across the table: the row's provenance reads as prose,
                                and the layout cannot drift out of step with the column count. */}
                            <td colSpan={cols} className="xs">
                              {r.sale.source ? (
                                <div>
                                  <span className="strong">Sale</span> — tied to {MATCH_LABEL[r.sale.source]}
                                  {r.sale.sharedBy > 1 ? `, split ${r.sale.sharedBy} ways` : ''}.{' '}
                                  {opp && (
                                    <>
                                      Opportunity <span className="strong">{opp.title ?? `#${opp.id}`}</span>
                                      {opp.date ? ` (${day(opp.date)})` : ''}
                                      {'. '}
                                    </>
                                  )}
                                  {order ? (
                                    <>
                                      Sales order <span className="strong">{order.title ?? `#${order.id}`}</span>
                                      {order.date ? ` of ${day(order.date)}` : ''}
                                      {order.status ? `, ${order.status}` : ''}.{' '}
                                    </>
                                  ) : (
                                    'No sales order was raised from it. '
                                  )}
                                  {r.sale.quoteNumber && (
                                    <>
                                      Quote <span className="mono">{r.sale.quoteNumber}</span>
                                      {r.sale.lineName ? `, line “${r.sale.lineName}”` : ''}.{' '}
                                    </>
                                  )}
                                  {money(r.sale.price)} less {costVisible ? money(r.sale.cost) : 'a cost Autotask will not show'}
                                  {costVisible ? ` is ${money(r.sale.profit)}` : ''}.
                                </div>
                              ) : (
                                <div className="muted">
                                  <span className="strong">Sale</span> — nothing found. The configuration item carries no quote line, and no won opportunity
                                  at {r.client} names its serial, product or model.
                                </div>
                              )}
                              <div className="mt-1">
                                <span className="strong">Renewal</span>
                                {r.renewal.source === 'opportunity' ? (
                                  <>
                                    {' '}
                                    — from the renewal opportunity{' '}
                                    <span className="strong">{r.renewal.opportunity?.title ?? `#${r.renewal.opportunity?.id}`}</span>
                                    {r.renewal.opportunity?.date ? ` (${day(r.renewal.opportunity.date)})` : ''}
                                    {r.renewal.sharedBy > 1 ? `, split ${r.renewal.sharedBy} ways` : ''}
                                    {r.renewal.salesOrder ? `, on sales order ${r.renewal.salesOrder.title ?? `#${r.renewal.salesOrder.id}`}` : ''}:{' '}
                                    {money(r.renewal.annualPrice)} a year.
                                  </>
                                ) : r.renewal.source === 'contract' ? (
                                  <>
                                    {' '}
                                    — no renewal opportunity, so from the contract service{' '}
                                    <span className="strong">{r.renewal.service}</span>
                                    {r.renewal.period ? ` billed ${r.renewal.period}` : ''}: {money(r.renewal.annualPrice)} a year.
                                  </>
                                ) : (
                                  <span className="muted"> — no renewal opportunity and no contract service, so the money is left blank.</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {shown.length > 1 && (
                    <tr className="cr-total">
                      <td>{num(shown.length)} firewalls</td>
                      <td className="xs">{num(new Set(shown.map((r) => r.clientId)).size)} clients</td>
                      <td className="xs">{num(shown.filter((r) => r.sale.salesOrder !== null).length)} with an order</td>
                      <td className="num">{money(price)}</td>
                      {costVisible && <td className="num">{money(sum((r) => r.sale.cost))}</td>}
                      {costVisible && <td className="num">{money(profit)}</td>}
                      <td className="num xs">{marginOf(profit, price) === null || !costVisible ? '—' : `${marginOf(profit, price)}%`}</td>
                      <td className="xs">
                        {num(shown.filter((r) => r.renewal.state === 'expired').length)} expired ·{' '}
                        {num(shown.filter((r) => r.renewal.state === 'soon').length)} due
                      </td>
                      <td className="num">{money(renewalPrice)}</td>
                      <td className="num">{costVisible ? money(renewalProfit) : '—'}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card mt-4">
            <div className="card-head">
              <div className="grow">
                <h3>Where these figures came from</h3>
                <p className="sub">Read live from Autotask; nothing is stored and nothing is written back.</p>
              </div>
            </div>
            <div className="card-body">
              <dl className="xs">
                <dt className="strong">Entities</dt>
                <dd className="muted mb-2">{d.source.entities}</dd>
                <dt className="strong">Filter</dt>
                <dd className="muted mb-2">{d.source.filter}</dd>
                <dt className="strong">Fields</dt>
                <dd className="muted mb-2">{d.source.fields}</dd>
                <dt className="strong">Formula</dt>
                <dd className="muted">{d.source.formula}</dd>
              </dl>
            </div>
          </section>
        </>
      )}
    </>
  );
}
